import {type ReactNode} from 'react'
import {useRouter} from 'next/router'
import _ from 'lodash'
import {setCookie} from 'cookies-next'
// Custom hooks
import {useBoundRoute} from '../../hooks/use-bound-route'
// Types
import type {BallotDisplayType} from '../../types/ballot'
// Utils
import {convertHoursToSeconds} from '../../utils/number-helpers'
// Styles
import styles from './entry-images-mobile-full-screen.module.scss'

interface EntryAdditionalFormField {
  id?: string
  rank?: number | string
  field_label?: string
  type?: string
  value?: any
  hide_field_in_voting_and_gallery_view?: boolean
}

interface BallotFormFieldDropDownChoice {
  rank?: number | string
  name?: string
}

interface BallotAdditionalFormField {
  id?: string
  drop_down_choices?: BallotFormFieldDropDownChoice[]
  hide_field_in_voting_and_gallery_view?: boolean
}

interface EntryWithImages {
  _id?: string
  photo_name?: string
  photo_image?: string
  photo_description?: string
  photo_images_array?: string[]
  cover_image?: string
  additional_form_fields?: EntryAdditionalFormField[]
}

interface VoteObject {
  photoImage?: string
  photoName?: string
  additionalFormFields?: EntryAdditionalFormField[]
}

interface EntryImagesMobileFullScreenProps {
  entry: EntryWithImages
  ballotAdditionalFormFields?: BallotAdditionalFormField[]
  currentIndex: number
  hasVoter: boolean
  isShowingPaginationControls: boolean
  onCastVote: (voteObject: VoteObject) => void
  onCloseEntryImagesModal: () => void
  children: ReactNode
  ballotDisplayType?: BallotDisplayType
}

const EntryImagesMobileFullScreen = ({
  entry,
  ballotAdditionalFormFields,
  currentIndex,
  hasVoter,
  isShowingPaginationControls,
  onCastVote,
  onCloseEntryImagesModal,
  children,
  ballotDisplayType,
}: EntryImagesMobileFullScreenProps) => {
  const boundRoute = useBoundRoute()
  const router = useRouter()

  const castVoteAndCloseModal = (selectedEntry: EntryWithImages) => {
    if (_.isEmpty(selectedEntry)) return null
    if (!hasVoter) {
      setCookie('previous_pathname', router.asPath || '/', {maxAge: convertHoursToSeconds(90), path: '/'})
      const signinRoute = boundRoute('/auth/signin')
      if (signinRoute) router.push(signinRoute)
      return
    }
    const voteObject = {
      photoImage: selectedEntry.photo_image,
      photoName: selectedEntry.photo_name,
      additionalFormFields: selectedEntry.additional_form_fields,
    }
    onCastVote(voteObject)
    onCloseEntryImagesModal()
  }
  return entry?.photo_images_array
    ? <>
      <div className={`${styles['mobile-full-screen']} ${isShowingPaginationControls ? styles['--is-showing-pagination-controls'] : ''}`}>
        <div className={styles['__header']}>
          {entry?.photo_name && (
            <h2 className={styles['__title-text']}>{entry.photo_name}</h2>
          )}
          <button
            type='button'
            onClick={onCloseEntryImagesModal}
            className={`button is-medium ${styles['__close-button']}`}
          >
            <span className={styles['__button-text']}>{'Close \u2716'}</span>
          </button>
        </div>
        <div className={styles['__subheader']}>
          <h3 className={styles['__pagination-text']}>
            {`${currentIndex + 1} of ${entry.photo_images_array?.length}`}
          </h3>
        </div>
        <div className={styles['__container']}>
          <div className={styles['__content']}>
            {children}
            <div className={styles['__additional-fields']}>
              {entry?.photo_description && <p className={styles['__description-text']}>{entry.photo_description}</p>}
              {_.map(entry?.additional_form_fields, (entryAdditionalFormField, index) => {
                const ballotField = _.find(ballotAdditionalFormFields, bf => bf.id === entryAdditionalFormField.id)
                const isHidden = entryAdditionalFormField.hide_field_in_voting_and_gallery_view
                  || ballotField?.hide_field_in_voting_and_gallery_view
                if (!isHidden) {
                  if (entryAdditionalFormField.value) {
                    if (entryAdditionalFormField.type === 'textfield') {
                      return (
                        <div
                          key={`${entryAdditionalFormField.rank}-${index}`}
                          className={styles['__additional-field-container']}
                        >
                          <h5 className={styles['__additional-field-label']}>{entryAdditionalFormField.field_label}</h5>
                          <p className={`${styles['__additional-field']} ${styles['--textfield']}`}>
                            {entryAdditionalFormField.value}
                          </p>
                        </div>
                      )
                    }
                    if (entryAdditionalFormField.type === 'textarea') {
                      return (
                        <div
                          key={`${entryAdditionalFormField.rank}-${index}`}
                          className={styles['__additional-field-container']}
                        >
                          <h5 className={styles['__additional-field-label']}>{entryAdditionalFormField.field_label}</h5>
                          <p className={`${styles['__additional-field']} ${styles['--textarea']}`}>
                            {entryAdditionalFormField.value}
                          </p>
                        </div>
                      )
                    }
                    if (entryAdditionalFormField.type === 'checkbox') {
                      return (
                        <div
                          key={`${entryAdditionalFormField.rank}-${index}`}
                          className={styles['__additional-field-container']}
                        >
                          <h5 className={styles['__additional-field-label']}>{entryAdditionalFormField.field_label}</h5>
                          <p className={`${styles['__additional-field']} ${styles['--checkbox']}`}>
                            {entryAdditionalFormField.value ? 'Yes' : 'No'}
                          </p>
                        </div>
                      )
                    }
                    if (entryAdditionalFormField.type === 'select') {
                      const foundBallotField = _.find(ballotAdditionalFormFields, ballotFormField => {
                        return ballotFormField.id === entryAdditionalFormField.id
                      })
                      const selectOptionName = _.find(foundBallotField?.drop_down_choices, choice => {
                        return choice.rank === entryAdditionalFormField.value
                      })?.name

                      return (
                        <div
                          key={`${entryAdditionalFormField.rank}-${index}`}
                          className={styles['__additional-field-container']}
                        >
                          <h5 className={styles['__additional-field-label']}>{entryAdditionalFormField.field_label}</h5>
                          <p className={`${styles['__additional-field']} ${styles['--select']}`}>
                            {selectOptionName}
                          </p>
                        </div>
                      )
                    }
                    if (entryAdditionalFormField.type === 'imageupload') {
                      return null
                      // return (
                      //   <div
                      //     key={`${entryAdditionalFormField.rank}-${index}`}
                      //     className={styles['__additional-field-container']}
                      //   >
                      //     <h5 className={styles['__additional-field-label']}>{entryAdditionalFormField.field_label}</h5>
                      //     <img
                      //       src={entryAdditionalFormField.value}
                      //       className={`${styles['__additional-field']} ${styles['--imageupload']}`}
                      //     />
                      //   </div>
                      // )
                    }
                  }
                }
              })}
            </div>
          </div>
        </div>
        {!_.includes(['photo-voting-results', 'photo-voting-finalist-announcement'], ballotDisplayType) &&
          <button
            type='button'
            onClick={() => castVoteAndCloseModal(entry)}
            className={`button is-medium is-fullwidth ${styles['__vote-button']}`}
          >
            <span className={styles['__button-text']}>
              {entry?.photo_name ? `Vote for ${entry?.photo_name}` : 'Cast vote'}
            </span>
          </button>
        }
      </div>
    </>
    : null
}

export default EntryImagesMobileFullScreen
